import { useState, useEffect } from 'react';
import { GearExchangeImportButton } from '../gear-exchange/GearExchangeImportButton';
import type { FieldIssue } from '../../types';
import {
  CharCount,
  FieldLabel,
  InlineIssue,
  Field,
  AssetSlot,
  inputClass,
  issueBorder,
} from './shared';
import type { UploadRatio } from './shared';
import type { Campaign, ValidationFeedback } from '@/api/campaigns';
import { GearExchangeModal, type GXImportedFile } from '../gear-exchange/GearExchangeModal';

// ─── Constants ────────────────────────────────────────────────────────────────

const INTRO_LIMIT = 150;
const HEADLINE_LIMIT = 70;

const CTA_OPTIONS = [
  'Apply',
  'Download',
  'View Quote',
  'Learn More',
  'Sign Up',
  'Subscribe',
  'Register',
  'Join',
  'Attend',
  'Request Demo',
];

// ─── Main component ───────────────────────────────────────────────────────────

interface LinkedInDetailsInitialValues {
  existingImageUrl?: string;
  imageContentType?: string | null;
  existingImageUrl4x5?: string;
  imageContentType4x5?: string | null;
  url?: string;
  legal?: string;
}

interface LinkedInDetailsProps {
  campaignId?: string;
  fordshareIds?: string[] | null;
  campaign?: Campaign | null;
  showErrors: boolean;
  showWarnings: boolean;
  fieldIssues: Record<string, FieldIssue>;
  file1x1: File | null;
  onFile1x1: (f: File | null) => void;
  file4x5: File | null;
  onFile4x5: (f: File | null) => void;
  /** Which of the two feed slots the shared LinkedIn mockup is showing */
  activeSlot?: '1x1' | '4x5';
  onSlotSelect?: (slot: '1x1' | '4x5') => void;
  caption: string;
  onCaptionChange: (v: string) => void;
  headline: string;
  onHeadlineChange: (v: string) => void;
  cta: string;
  onCtaChange: (v: string) => void;
  onWebsiteChange: (v: string) => void;
  onUrlChange: (v: string) => void;
  onLegalChange: (v: string) => void;
  onExistingImageClear?: (ratio: UploadRatio) => void;
  initialValues?: LinkedInDetailsInitialValues;
  feedback?: Record<string, ValidationFeedback>;
  feedbackPending?: Set<string>;
  onAIFeedback?: (
    warningKey: string,
    warningMessage: string,
    action: 'accepted' | 'dismissed',
  ) => void;
  usedInVariants?: Map<string, number[]>;
  gxData?: import('@/api/gearExchange').GXMultiSearchResponse | null;
  gxLoading?: boolean;
  gxError?: boolean;
}

export function LinkedInDetails({
  campaignId,
  fordshareIds,
  campaign,
  showErrors,
  showWarnings,
  fieldIssues,
  file1x1,
  onFile1x1: setFile1x1,
  file4x5,
  onFile4x5,
  activeSlot,
  onSlotSelect,
  caption,
  onCaptionChange,
  headline,
  onHeadlineChange,
  cta,
  onCtaChange,
  onWebsiteChange,
  onUrlChange,
  onLegalChange,
  onExistingImageClear,
  initialValues,
  feedback,
  feedbackPending,
  onAIFeedback,
  usedInVariants,
  gxData,
  gxLoading,
  gxError,
}: LinkedInDetailsProps) {
  const [destinationUrl, setDestinationUrl] = useState(initialValues?.url ?? '');
  const [legal, setLegal] = useState(initialValues?.legal ?? '');
  const [clearedImage, setClearedImage] = useState(false);
  const [cleared4x5, setCleared4x5] = useState(false);

  // Gear Exchange state
  const [showGxModal, setShowGxModal] = useState(false);
  // Holds WHICH slot is pending deletion, not just "a delete is pending" — with
  // two slots a boolean can't tell them apart.
  const [pendingClear, setPendingClear] = useState<UploadRatio | null>(null);

  // Sync initial values to parent on mount
  useEffect(() => {
    onUrlChange(initialValues?.url ?? '');
    onLegalChange(initialValues?.legal ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleDestinationChange(v: string) {
    setDestinationUrl(v);
    onUrlChange(v);
    try {
      onWebsiteChange(v ? new URL(v).hostname : '');
    } catch {
      onWebsiteChange('');
    }
  }

  return (
    <>
      {pendingClear && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-80 bg-white rounded-xl border border-slate-200 shadow-xl flex flex-col overflow-hidden">
            <div className="px-6 py-3 border-b border-slate-200 bg-slate-50 rounded-t-xl flex-shrink-0">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
                Delete Asset
              </p>
            </div>
            <div className="px-6 py-5">
              <p className="text-sm text-slate-600">
                Are you sure you want to remove the saved asset? You will still need to press Save
                to apply this change.
              </p>
              <div className="flex gap-2 mt-5 justify-end">
                <button
                  onClick={() => setPendingClear(null)}
                  className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (!pendingClear) return;
                    // Exhaustive map, not an if/else: a new UploadRatio must be a
                    // compile error here rather than silently clearing the wrong slot.
                    const setCleared: Record<UploadRatio, (v: boolean) => void> = {
                      '1x1': setClearedImage,
                      '4x5': setCleared4x5,
                      '9x16': () => {},
                    };
                    setCleared[pendingClear](true);
                    onExistingImageClear?.(pendingClear);
                    setPendingClear(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="flex-1 overflow-y-auto hover-scroll px-6 py-5 space-y-6 min-h-0">
        {/* ── About ───────────────────────────────────────────────────── */}
        <div className="border-l-2 border-[#0014cc]/30 pl-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
            About this format
          </p>
          <p className="text-sm text-slate-600 leading-relaxed">
            A <span className="font-medium text-slate-700">1:1 or 4:5 image or video</span> shown as
            a sponsored post that appears in the LinkedIn feed. Includes introductory text above the
            creative, a headline and CTA button in the blue bar below.{' '}
            <a
              href="https://business.linkedin.com/advertise/ads/sponsored-content/single-image-ads-specs"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-400 hover:text-slate-600 underline underline-offset-2 decoration-slate-300"
            >
              LinkedIn ad specs ↗
            </a>
          </p>
        </div>

        {/* ── Gear Exchange Import ──────────────────────────────────── */}
        {
          <>
            <GearExchangeImportButton
              totalAssets={gxData?.total_assets ?? null}
              loading={gxLoading}
              error={gxError}
              fordshareIds={fordshareIds ?? undefined}
              onClick={() => setShowGxModal(true)}
            />

            {showGxModal && gxData && campaign && campaignId && (
              <GearExchangeModal
                data={gxData}
                campaign={campaign}
                campaignId={campaignId}
                formatLabel="LinkedIn Single"
                zoneLimits={[
                  {
                    ratio: '1x1',
                    limit: 1,
                    existing: file1x1 || (initialValues?.existingImageUrl && !clearedImage) ? 1 : 0,
                  },
                  {
                    ratio: '4x5',
                    limit: 1,
                    existing:
                      file4x5 || (initialValues?.existingImageUrl4x5 && !cleared4x5) ? 1 : 0,
                  },
                ]}
                onClose={() => setShowGxModal(false)}
                usedInVariants={usedInVariants}
                onImport={(files: GXImportedFile[]) => {
                  // Route by ratio — taking files[0] blindly dropped a 4:5 into
                  // the 1:1 slot.
                  const f1x1 = files.find((f) => f.ratio === '1x1');
                  const f4x5 = files.find((f) => f.ratio === '4x5');
                  if (f1x1) {
                    setFile1x1(f1x1.file);
                    setClearedImage(true);
                  }
                  if (f4x5) {
                    onFile4x5(f4x5.file);
                    setCleared4x5(true);
                  }
                }}
              />
            )}
          </>
        }

        {/* ── Assets ──────────────────────────────────────────────────── */}
        <div>
          {/* No section heading here — each AssetSlot renders its own label, and Meta
              Single lists its slots the same way. */}
          {/* Both slots feed the same mockup, so only one shows at a time —
              clicking a filled slot chooses which. Same pattern as Meta Single. */}
          <div className="grid grid-cols-2 gap-x-4">
            <AssetSlot
              label="1:1 Asset"
              tooltip="Square feed asset. Provide a 1:1 or a 4:5 (both is best). 1080x1080px recommended."
              hint="Click to upload 1:1 image or video"
              ratio="1x1"
              fieldKey="asset_files"
              file={file1x1}
              onFile={setFile1x1}
              existingUrl={clearedImage ? undefined : initialValues?.existingImageUrl}
              existingContentType={initialValues?.imageContentType}
              onClearExisting={() => setPendingClear('1x1')}
              fieldIssues={fieldIssues}
              showErrors={showErrors}
              showWarnings={showWarnings}
              feedback={feedback}
              feedbackPending={feedbackPending}
              onFeedback={onAIFeedback}
              isActive={activeSlot === '1x1'}
              onSelect={onSlotSelect ? () => onSlotSelect('1x1') : undefined}
            />
            <AssetSlot
              label="4:5 Asset"
              tooltip="Vertical feed asset. Provide a 1:1 or a 4:5 (both is best). 1080x1350px recommended."
              hint="Click to upload 4:5 image or video"
              ratio="4x5"
              fieldKey="asset_files"
              file={file4x5}
              onFile={onFile4x5}
              existingUrl={cleared4x5 ? undefined : initialValues?.existingImageUrl4x5}
              existingContentType={initialValues?.imageContentType4x5}
              onClearExisting={() => setPendingClear('4x5')}
              fieldIssues={fieldIssues}
              showErrors={showErrors}
              showWarnings={showWarnings}
              feedback={feedback}
              feedbackPending={feedbackPending}
              onFeedback={onAIFeedback}
              isActive={activeSlot === '4x5'}
              onSelect={onSlotSelect ? () => onSlotSelect('4x5') : undefined}
            />
          </div>
        </div>

        {/* ── Introductory Text ───────────────────────────────────────── */}
        <div>
          <FieldLabel
            label="Primary Text"
            right={<CharCount value={caption} limit={INTRO_LIMIT} />}
            tooltip="The post body text shown above the image. Recommended 150 chars."
          />
          <textarea
            value={caption}
            onChange={(e) => onCaptionChange(e.target.value)}
            placeholder="Write something that will make your audience stop scrolling..."
            rows={4}
            className={`${inputClass} resize-none ${issueBorder(fieldIssues['primary_text'], showErrors, showWarnings)}`}
          />
          <InlineIssue
            issue={fieldIssues['primary_text']}
            showErrors={showErrors}
            showWarnings={showWarnings}
            feedback={feedback}
            feedbackPending={feedbackPending}
            onFeedback={onAIFeedback}
          />
        </div>

        {/* ── Headline ────────────────────────────────────────────────── */}
        <div>
          <FieldLabel
            label="Headline"
            right={<CharCount value={headline} limit={HEADLINE_LIMIT} />}
            tooltip="Bold text shown in the blue bar below the image. Max 70 chars."
          />
          <input
            type="text"
            value={headline}
            onChange={(e) => onHeadlineChange(e.target.value)}
            placeholder="Short, action-driven headline..."
            className={`${inputClass} ${issueBorder(fieldIssues['headline'], showErrors, showWarnings)}`}
          />
          <InlineIssue
            issue={fieldIssues['headline']}
            showErrors={showErrors}
            showWarnings={showWarnings}
            feedback={feedback}
            feedbackPending={feedbackPending}
            onFeedback={onAIFeedback}
          />
        </div>

        {/* ── Destination URL + CTA ───────────────────────────────────── */}
        <div className="grid grid-cols-2 gap-x-4">
          <div>
            <FieldLabel
              label="Destination URL"
              tooltip="Landing page URL. Must begin with http:// or https://. Max 2000 chars."
            />
            <input
              type="url"
              value={destinationUrl}
              onChange={(e) => handleDestinationChange(e.target.value)}
              placeholder="https://www.ford.com/..."
              className={`${inputClass} ${issueBorder(fieldIssues['destination_url'], showErrors, showWarnings)}`}
            />
            <InlineIssue
              issue={fieldIssues['destination_url']}
              showErrors={showErrors}
              showWarnings={showWarnings}
              feedback={feedback}
              feedbackPending={feedbackPending}
              onFeedback={onAIFeedback}
            />
          </div>
          <div>
            <FieldLabel
              label="Call to Action"
              tooltip="Button shown in the blue bar alongside the headline."
            />
            <select
              value={cta}
              onChange={(e) => onCtaChange(e.target.value)}
              className={`${inputClass} ${issueBorder(fieldIssues['call_to_action'], showErrors, showWarnings)}`}
            >
              {CTA_OPTIONS.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
            <InlineIssue
              issue={fieldIssues['call_to_action']}
              showErrors={showErrors}
              showWarnings={showWarnings}
              feedback={feedback}
              feedbackPending={feedbackPending}
              onFeedback={onAIFeedback}
            />
          </div>
        </div>

        {/* ── Legal ───────────────────────────────────────────────────── */}
        <Field
          label="Legal"
          tooltip="Required in some markets (e.g. France) for regulated categories. Internal only — not displayed in the mockup."
          fieldKey="legal"
          fieldIssues={fieldIssues}
          showErrors={showErrors}
          showWarnings={showWarnings}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onFeedback={onAIFeedback}
          help={<>Enter "N/A" if your market does not require a legal disclaimer.</>}
        >
          <textarea
            value={legal}
            onChange={(e) => {
              setLegal(e.target.value);
              onLegalChange(e.target.value);
            }}
            placeholder="Legal disclaimer — required for some markets (e.g. FR)..."
            rows={5}
            className={`${inputClass} resize-none ${issueBorder(fieldIssues['legal'], showErrors, showWarnings)}`}
          />
        </Field>
      </div>
    </>
  );
}
